function TermsOfUseRpg() {
  return (
    <div>
      <h2>Rural Physicians Group - Terms of Service for Compass AI</h2>
      <p>
        <strong>Last Updated:</strong> <span>August 1, 2025</span>
      </p>

      <p>
        This Terms of Service agreement (this <strong>“Agreement”</strong>) is a legally binding contract between the individual
        or entity agreeing to these terms (<strong>“User”</strong>, <strong>“you”</strong>, or <strong>“your”</strong>) and Rural
        Physicians Group, a Colorado Limited Liability Company (<strong>“RPG”</strong>, <strong>“we”</strong>,{" "}
        <strong>“us”</strong>, or
        <strong>“our”</strong>). This Agreement governs your access to and use of the Compass AI platform, including any related
        software, services, and websites (collectively, the <strong>“Platform”</strong>).
      </p>

      <p>
        The Platform is powered by a proprietary ambient artificial intelligence transcription software engine and related
        technology (the <strong>"Software"</strong>) licensed from Lucas Health Corporation (the{" "}
        <strong>"Technology Provider"</strong>).
      </p>

      <p>
        By creating an account, clicking <em>“I accept”</em>, or by accessing or using the Platform in any manner, you affirm that
        you have read, fully understand, and agree to be legally bound by this entire Agreement. If you are entering into this
        Agreement on behalf of a company or other legal entity, you represent and warrant that you have the full legal authority
        to bind such entity to this Agreement.
      </p>

      <p>
        <strong>
          THIS AGREEMENT CONTAINS A MANDATORY ARBITRATION PROVISION, A WAIVER OF CLASS ACTION RIGHTS, AND COMPREHENSIVE
          INDEMNIFICATION OBLIGATIONS. IT PLACES SOLE AND ULTIMATE RESPONSIBILITY ON YOU FOR THE ACCURACY OF ALL DOCUMENTATION AND
          FOR ANY FINANCIAL OR REGULATORY CONSEQUENCES, INCLUDING AUDITS AND CLAWBACKS. THE PLATFORM IS PROVIDED WITHOUT ANY
          GUARANTEE OF ACCURACY. PLEASE READ THIS DOCUMENT CAREFULLY IN ITS ENTIRETY.
        </strong>
      </p>

      <h3>The Platform: Purpose and Core Limitations</h3>
      <p>
        The Platform is an ambient artificial intelligence transcription and documentation support tool designed to improve
        workflow efficiencies for qualified healthcare providers (<strong>“Providers”</strong>) by capturing and documenting
        patient-provider interactions. The Platform is a tool to assist with documentation, not to create final, verified records.
        All ultimate responsibility for clinical decisions, documentation accuracy, and regulatory compliance remains with you.
      </p>

      <h3>Account and User Security</h3>
      <p>
        You must provide accurate and current information upon registration and are solely responsible for all activity under your
        account. You must implement and maintain reasonable administrative, physical, and technical safeguards to protect your
        account credentials and notify RPG immediately in writing of any unauthorized use or security breach related to your
        account. Neither RPG nor its Technology Provider is liable for losses resulting from your failure to comply with these
        obligations.
      </p>

      <h3>Intellectual Property and Platform Ownership</h3>
      <p>
        <strong>3.1. Ownership.</strong> You acknowledge that the Platform, including the underlying Software, source code,
        architecture, algorithms, and all associated intellectual property rights, is the sole and exclusive property of our
        Technology Provider, Lucas Health Corporation. The Platform is licensed to you on a limited basis; it is not sold.
      </p>
      <p>
        <strong>3.2. Restrictions.</strong> You are prohibited from creating derivative works, accessing non-public source
        materials, sublicensing, selling, renting, or otherwise commercializing the Platform, or copying, decompiling, modifying,
        or reverse engineering the Platform or Software.
      </p>
      <p>
        <strong>3.3. Trademarks.</strong> The marks Compass AI, Rural Physicians Group, and our associated logos are our exclusive
        trademarks. The marks LUCAS HEALTH and LUCAS AI are the exclusive trademarks of our Technology Provider.
      </p>
      <p>
        <strong>3.4. User Data Ownership and License.</strong> You retain ownership of your User Data. You grant RPG and Lucas
        Health Corporation a worldwide, non-exclusive, perpetual, irrevocable, royalty-free, transferable, and sublicensable
        license to copy, edit, modify, distribute, anonymize, aggregate, and use your User Data as necessary to provide, maintain,
        secure, test, and improve the Platform, including for training AI models.
      </p>
      <p>
        <strong>3.5. Feedback.</strong> Any feedback you submit will be the sole property of RPG and its Technology Provider and
        may be used without acknowledgment or compensation.
      </p>

      <h3>Prohibited Conduct and Intended Use</h3>
      <p>Your use of the Platform must be legal, ethical, and for its intended use only. You are prohibited from using it to:</p>
      <ul>
        <li>Create false, misleading, fraudulent, or artificially generated medical documentation;</li>
        <li>
          Manufacture compliance documentation for services not authentically rendered or for patient encounters that did not
          occur;
        </li>
        <li>Generate documentation to reverse-engineer or artificially create medical compliance records;</li>
        <li>
          Facilitate, justify, or encourage the overutilization or misuse of medical treatments, biologics, tissues, or
          pharmaceuticals;
        </li>
        <li>
          Engage in activity that constitutes or facilitates Medicare/Medicaid/insurance fraud or healthcare billing abuse, or
          violates applicable laws (including Anti-Kickback, Stark, or False Claims Act).
        </li>
      </ul>

      <h3>Indemnification</h3>
      <p>
        You agree to defend, indemnify, and hold harmless RPG, Lucas Health Corporation, and related parties from and against any
        and all third-party claims, losses, damages, liabilities, fines, penalties, and expenses (including attorneys' fees)
        arising out of or related to:
      </p>
      <ul>
        <li>Your material breach of this Agreement or violation of law;</li>
        <li>Claims related to your User Data;</li>
        <li>Your use, misuse, or inability to use the Platform;</li>
        <li>Your clinical malpractice, negligence, or violation of professional standards;</li>
        <li>Your documentation, billing, or coding practices, including alleged fraudulent documentation or billing fraud;</li>
        <li>Any custom guidelines, templates, or protocols you provide that violate laws or regulations;</li>
        <li>
          Claims arising from inaccuracies, incompleteness, or insufficiency of documentation generated by the Platform that you
          failed to independently verify and correct.
        </li>
      </ul>

      <h3>Warranty Exclusions and Limitation of Liability</h3>
      <p>
        <strong>6.1. WARRANTY DISCLAIMER.</strong> THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE," WITH ALL FAULTS AND
        WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, RPG AND ITS TECHNOLOGY PROVIDER EXPRESSLY DISCLAIM
        ANY AND ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY.
      </p>
      <p>
        <strong>6.2. EXCLUSION OF DAMAGES.</strong> UNDER NO CIRCUMSTANCES WILL RPG OR ITS TECHNOLOGY PROVIDER BE LIABLE FOR ANY
        INCIDENTAL, CONSEQUENTIAL, INDIRECT, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES.
      </p>
      <p>
        <strong>6.3. MAXIMUM AGGREGATE LIABILITY.</strong> IN NO EVENT SHALL THE TOTAL AGGREGATE LIABILITY OF RPG AND ITS
        TECHNOLOGY PROVIDER EXCEED THE GREATER OF (A) THE TOTAL FEES PAID BY YOU TO RPG DURING THE TWELVE (12) MONTHS PRECEDING
        THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100.00).
      </p>

      <h3>User's Sole Responsibility for Verification, Accuracy, and All Outcomes</h3>
      <p>
        <strong>7.1. Acknowledgment of AI Limitations.</strong> You understand that the Platform uses AI and the output is an
        automated draft that may contain inaccuracies, errors, or omissions.
      </p>
      <p>
        <strong>7.2. Absolute Duty to Verify.</strong> You must independently review, edit, and verify every part of the output
        before signing, approving, or using it for any purpose, including entry into a medical record, clinical decision-making,
        or billing submissions.
      </p>
      <p>
        <strong>7.3. Sole Liability for All Consequences.</strong> You are solely liable for all content within your final
        clinical documentation and for any outcomes resulting from that documentation. RPG and Lucas Health Corporation bear no
        liability for adverse consequences arising from your use of the Platform's output.
      </p>
      <p>
        <strong>7.4. Advisory Nature.</strong> All tools and features are advisory and not a substitute for professional medical,
        legal, or billing judgment.
      </p>

      <h3>Lucas Health Corporation as Third-Party Beneficiary</h3>
      <p>
        Lucas Health Corporation is an intended third-party beneficiary of this Agreement and may enforce provisions directly
        against you. Notwithstanding, your sole recourse for claims arising from this Agreement shall be exclusively against RPG.
      </p>

      <h3>Termination</h3>
      <p>
        We may suspend or terminate your account and this Agreement immediately for any material breach. Upon termination, your
        right to use the Platform ceases. Sections 3, 4, 5, 6, 7, 8, 10, and 11 will survive termination.
      </p>

      <h3>Governing Law and Dispute Resolution</h3>
      <p>
        This Agreement is governed by the laws of the State of Colorado. All disputes shall be resolved exclusively by final and
        binding arbitration in Arapahoe County, Colorado. YOU AND RPG WAIVE THE RIGHT TO BRING CLASS ACTIONS.
      </p>

      <h3>Miscellaneous Provisions</h3>
      <p>
        <strong>11.1. Entire Agreement.</strong> This Agreement, along with our Privacy Policy and Business Associate Agreement
        (if applicable), is the entire agreement between you and RPG.
      </p>
      <p>
        <strong>11.2. Severability.</strong> If any provision is found unenforceable it will be limited or eliminated to the
        minimum extent necessary so the Agreement remains in force.
      </p>
      <p>
        <strong>11.3. Contact Information.</strong> For legal notices, contact us at: legal@ruralphysiciansgroup.com or 5575 DTC
        Parkway, Ste 225, Greenwood Village, CO 80111.
      </p>
    </div>
  );
}

export default TermsOfUseRpg;
